/**
 * The shift (X / Z) report.
 *
 * The document a manager signs at closing. Its whole purpose is the last block:
 * what the system says should be in the drawer, what was actually counted, and
 * the difference. Everything above it exists to explain that number.
 */

import { money, formatDateTime } from '../../utils/format';

const Row = ({ label, value, bold = false, indent = false }) => (
    <tr className={bold ? 'font-bold' : ''}>
        <td className={indent ? 'pl-3' : ''}>{label}</td>
        <td className="num">{value}</td>
    </tr>
);

const ShiftReport = ({ report, countedCash = null, isPreview = false }) => {
    if (!report) return null;

    const { business, session, sales, payments, drawer, voids } = report;
    const difference = countedCash != null ? countedCash - drawer.expectedCash : null;
    const currency = sales.currency || 'Rs.';

    return (
        <div
            // Flush-left when actually printing, not centred - see the same
            // comment on Receipt.jsx.
            className={`receipt receipt-80 ${isPreview ? 'mx-auto border border-dashed border-slate-300 p-3' : 'p-1'}`}
        >
            <p className="text-center text-[1.2em] font-bold leading-tight">{business.name}</p>
            <p className="text-center font-bold leading-tight">
                {countedCash != null ? 'Z REPORT - SHIFT CLOSE' : 'X REPORT - SHIFT READING'}
            </p>

            <div className="rule-solid" />

            <table>
                <tbody>
                    <Row label="Shift" value={session.sessionNumber} />
                    <Row label="Till" value={session.terminalName || '-'} />
                    <Row label="Opened by" value={session.openedBy || '-'} />
                    <Row label="Opened at" value={formatDateTime(session.openedAt)} />
                    <Row label="Printed at" value={formatDateTime(report.printedAt)} />
                </tbody>
            </table>

            <div className="rule-solid" />
            <p className="font-bold">SALES</p>
            <table>
                <tbody>
                    <Row label="Orders" value={sales.orderCount} />
                    <Row label="Guests" value={sales.guestCount} />
                    <Row label="Gross sales" value={money(sales.totalSales)} />
                    <Row label="Discounts given" value={money(sales.totalDiscount)} />
                    <Row label="Sales tax collected" value={money(sales.totalTax)} />
                    <Row label="Average ticket" value={money(sales.averageTicket)} />
                </tbody>
            </table>

            <div className="rule" />
            <p className="font-bold">TAKINGS BY METHOD</p>
            <table>
                <tbody>
                    <Row label="Cash" value={money(payments.cash)} />
                    <Row label="Card" value={money(payments.card)} />
                    <Row label="Other" value={money(payments.other)} />
                    <Row
                        label="Total"
                        value={money(payments.cash + payments.card + payments.other)}
                        bold
                    />
                </tbody>
            </table>

            {voids.count > 0 && (
                <>
                    <div className="rule" />
                    <p className="font-bold">VOIDS</p>
                    <table>
                        <tbody>
                            <Row label="Voided orders" value={voids.count} />
                            <Row label="Value" value={money(voids.amount)} />
                        </tbody>
                    </table>
                </>
            )}

            <div className="rule-solid" />
            <p className="font-bold">CASH DRAWER</p>
            <table>
                <tbody>
                    <Row label="Opening float" value={money(drawer.openingCash)} />
                    <Row label="+ Cash sales" value={money(drawer.cashSales)} />
                    <Row label="- Change given" value={money(drawer.changeGiven)} />
                    {drawer.cashIn > 0 && <Row label="+ Cash in" value={money(drawer.cashIn)} />}
                    {drawer.cashOut > 0 && <Row label="- Cash out" value={money(drawer.cashOut)} />}
                    <Row label="Expected in drawer" value={money(drawer.expectedCash)} bold />

                    {countedCash != null && (
                        <>
                            <Row label="Counted" value={money(countedCash)} bold />
                            <Row
                                label={difference === 0 ? 'Balanced' : difference > 0 ? 'OVER' : 'SHORT'}
                                value={money(Math.abs(difference))}
                                bold
                            />
                        </>
                    )}
                </tbody>
            </table>

            <div className="rule-solid" />

            {countedCash != null && (
                <div className="mt-6">
                    <p className="leading-tight">Counted by: ____________________</p>
                    <p className="mt-4 leading-tight">Approved by: ___________________</p>
                </div>
            )}

            <p className="mt-2 text-center text-[0.9em]">
                {currency} figures. Keep this with the day&apos;s takings.
            </p>

            <div className="h-6" />
        </div>
    );
};

export default ShiftReport;
